"use client";

import { useEffect, useRef, useState } from "react";
import {
  Phone, Mail, Clock, Info, ArrowUp, ArrowRight,
  Building2, Leaf, FileText, Globe, Monitor, Shield,
  Users, Wrench, Briefcase, BookOpen, Heart, GraduationCap,
  Calendar, Landmark, Megaphone, Receipt, Shirt, MapPin,
  HelpCircle,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

type Service = {
  id: string;
  name: string;
  description: string | null;
  phone: string | null;
  email: string | null;
  hours: string | null;
  misc: string | null;
  photoUrl: string | null;
  serviceType: string | null;
};

// ─── Mapping icônes par mots-clés dans le nom du service ─────────────────────

const ICON_MAP: [RegExp, LucideIcon][] = [
  [/réservation|salle/i,       Calendar],
  [/médiathèque|bibliothèque/i, BookOpen],
  [/ccas|social|aide/i,         Heart],
  [/communication|presse/i,     Megaphone],
  [/comptab|financ|budget/i,    Receipt],
  [/éducation|scolaire|école/i, GraduationCap],
  [/espace.?vert|jardin|nature/i, Leaf],
  [/état.?civil|naissance|mariage/i, FileText],
  [/étrang|immigration/i,       Globe],
  [/informatique|numérique/i,   Monitor],
  [/laverie/i,                  Shirt],
  [/police|sécurité/i,          Shield],
  [/ressource.?humaine|rh|personnel/i, Users],
  [/secrétariat|maire/i,        Briefcase],
  [/technique|travaux|voirie/i, Wrench],
  [/urbanisme|permis/i,         Landmark],
  [/sport|culture/i,            MapPin],
];

const ICON_COLORS = [
  { bg: "bg-blue-100",   text: "text-blue-600" },
  { bg: "bg-emerald-100", text: "text-emerald-600" },
  { bg: "bg-violet-100", text: "text-violet-600" },
  { bg: "bg-orange-100", text: "text-orange-600" },
  { bg: "bg-rose-100",   text: "text-rose-600" },
  { bg: "bg-sky-100",    text: "text-sky-600" },
  { bg: "bg-amber-100",  text: "text-amber-600" },
  { bg: "bg-teal-100",   text: "text-teal-600" },
  { bg: "bg-indigo-100", text: "text-indigo-600" },
  { bg: "bg-pink-100",   text: "text-pink-600" },
  { bg: "bg-lime-100",   text: "text-lime-600" },
  { bg: "bg-cyan-100",   text: "text-cyan-600" },
];

function getIcon(name: string): LucideIcon {
  for (const [pattern, Icon] of ICON_MAP) {
    if (pattern.test(name)) return Icon;
  }
  return HelpCircle;
}

function getColor(index: number) {
  return ICON_COLORS[index % ICON_COLORS.length];
}

// ─────────────────────────────────────────────────────────────────────────────

function slugify(name: string) {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

// ─── Carte service ────────────────────────────────────────────────────────────

function ServiceCard({ service, index }: { service: Service; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.06 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const hasInfos = service.phone || service.email || service.hours || service.misc;

  return (
    /* Wrapper d'entrée : gère opacity + translateY uniquement */
    <div
      ref={ref}
      className="transition-[opacity,transform] duration-700"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(24px)",
        transitionDelay: `${Math.min(index * 70, 280)}ms`,
      }}
    >
      {/* Card : gère le hover (shadow + border) indépendamment de l'entrée */}
      <div
        id={slugify(service.name)}
        className="scroll-mt-8 group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:border-slate-300 hover:shadow-lg"
      >
        {/* ── Header ── */}
        {service.photoUrl ? (
          <div className="relative h-52 overflow-hidden">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={service.photoUrl}
              alt={service.name}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
            <div className="absolute bottom-0 left-0 p-6">
              {service.serviceType && (
                <span className="mb-2 inline-block rounded-full bg-white/20 px-3 py-1 text-xs font-bold uppercase tracking-widest text-white/90 backdrop-blur-sm">
                  {service.serviceType}
                </span>
              )}
              <h2 className="text-2xl font-bold text-white drop-shadow">{service.name}</h2>
            </div>
          </div>
        ) : (
          <div
            className="relative overflow-hidden px-7 py-6"
            style={{ background: "linear-gradient(135deg, #1d2638 0%, #2d4a7a 100%)" }}
          >
            <div className="pointer-events-none absolute -right-6 -top-6 h-28 w-28 rounded-full bg-white/10 transition-transform duration-500 group-hover:scale-125" />
            <div className="pointer-events-none absolute -bottom-4 right-14 h-16 w-16 rounded-full bg-white/5 transition-transform duration-500 group-hover:scale-150" />
            {service.serviceType && (
              <span className="mb-2 inline-block rounded-full bg-white/15 px-3 py-1 text-xs font-bold uppercase tracking-widest text-white/80">
                {service.serviceType}
              </span>
            )}
            <h2 className="text-2xl font-bold text-white">{service.name}</h2>
          </div>
        )}

        {/* ── Corps ── */}
        <div className="px-7 py-6">
          {service.description && (
            <p className="text-base leading-7 text-slate-600 whitespace-pre-line">
              {service.description}
            </p>
          )}

          {/* ── Infos pratiques ── */}
          {hasInfos && (
            <div className="mt-6 grid gap-3 border-t border-slate-100 pt-6 sm:grid-cols-2">
              {service.phone && (
                <a
                  href={`tel:${service.phone.replace(/\s/g, "")}`}
                  className="flex items-start gap-3 rounded-xl bg-slate-50 px-4 py-3 transition-all duration-200 hover:bg-white hover:shadow-md hover:-translate-y-0.5"
                >
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm transition-colors duration-200 group-hover:bg-slate-100">
                    <Phone size={13} className="text-[#1d2638]" />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Téléphone</p>
                    <p className="mt-0.5 text-sm font-semibold text-slate-800">{service.phone}</p>
                  </div>
                </a>
              )}

              {service.email && (
                <a
                  href={`mailto:${service.email}`}
                  className="flex items-start gap-3 rounded-xl bg-slate-50 px-4 py-3 transition-all duration-200 hover:bg-white hover:shadow-md hover:-translate-y-0.5"
                >
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm">
                    <Mail size={13} className="text-[#1d2638]" />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">E-mail</p>
                    <p className="mt-0.5 break-all text-sm font-semibold text-slate-800">{service.email}</p>
                  </div>
                </a>
              )}

              {service.hours && (
                <div className="flex items-start gap-3 rounded-xl bg-slate-50 px-4 py-3 transition-colors duration-200 hover:bg-slate-100 sm:col-span-2">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm">
                    <Clock size={13} className="text-[#1d2638]" />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Horaires d'ouverture</p>
                    <p className="mt-0.5 whitespace-pre-line text-sm text-slate-700">{service.hours}</p>
                  </div>
                </div>
              )}

              {service.misc && (
                <div className="flex items-start gap-3 rounded-xl bg-amber-50 px-4 py-3 transition-colors duration-200 hover:bg-amber-100 sm:col-span-2">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm">
                    <Info size={13} className="text-amber-500" />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-amber-400">À savoir</p>
                    <p className="mt-0.5 whitespace-pre-line text-sm text-amber-900">{service.misc}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Retour sommaire ── */}
          <div className="mt-5 flex justify-end border-t border-slate-100 pt-4">
            <a
              href="#sommaire"
              className="group/back flex items-center gap-1.5 text-xs font-medium text-slate-400 transition-colors duration-200 hover:text-slate-700"
            >
              <ArrowUp
                size={13}
                className="transition-transform duration-200 group-hover/back:-translate-y-0.5"
              />
              Retour au sommaire
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────

export default function ServicesMunicipauxClient({
  services,
}: {
  services: Service[];
}) {
  return (
    <div className="mx-auto max-w-5xl px-6 py-12 space-y-8">

      {/* Ancre séparée pour que le scroll atterrisse avant la carte */}
      <div id="sommaire" className="-mt-4" />

      {/* ── Sommaire ── */}
      <div>
        <h2 className="mb-1 text-xl font-bold text-slate-900">Sommaire</h2>
        <p className="mb-5 text-sm text-slate-500">
          Cliquez sur un service pour y accéder directement
        </p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((s, i) => {
            const Icon = getIcon(s.name);
            const color = getColor(i);
            return (
              <a
                key={s.id}
                href={`#${slugify(s.name)}`}
                className="group/item flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3.5 shadow-sm transition-all duration-200 hover:border-[#1d2638] hover:bg-[#1d2638] hover:shadow-md"
              >
                <div className={[
                  "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-all duration-200",
                  color.bg, color.text,
                  "group-hover/item:bg-white/15 group-hover/item:text-white",
                ].join(" ")}>
                  <Icon size={18} />
                </div>
                <span className="flex-1 text-sm font-semibold text-slate-800 transition-colors duration-200 group-hover/item:text-white">
                  {s.name}
                </span>
                <ArrowRight
                  size={15}
                  className="shrink-0 text-slate-300 transition-all duration-200 group-hover/item:text-white group-hover/item:translate-x-0.5"
                />
              </a>
            );
          })}
        </div>
      </div>

      {/* ── Fiches services ── */}
      {services.map((s, i) => (
        <ServiceCard key={s.id} service={s} index={i} />
      ))}
    </div>
  );
}
